import { useLayoutEffect } from 'react';

/**
 * Keeps `<meta name="theme-color">` equal to the page's own background, so the browser's bars
 * read as part of the screen rather than a frame around it.
 *
 * Safari on iOS 26 ignores `theme-color` and paints the strip behind the status bar and the one
 * behind its toolbar with the `body`'s background colour — one solid colour for both edges, which
 * is why each layout sets the `body`'s colour to the colour its own edges close in (see
 * `global.css`). Chrome on Android and older Safari read `theme-color` instead; this hook hands
 * them the same colour, re-read whenever the layout, the theme or the width can have changed it.
 */
export function useEdgeColor(): void {
  useLayoutEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta === null) {
      return undefined;
    }
    const sync = () => {
      const colour = getComputedStyle(document.body).backgroundColor;
      if (
        colour !== '' &&
        colour !== 'rgba(0, 0, 0, 0)' &&
        meta.getAttribute('content') !== colour
      ) {
        meta.setAttribute('content', colour);
      }
    };
    sync();

    // `data-theme` on <html> is how a chosen theme is applied.
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    const queries =
      typeof window.matchMedia === 'function'
        ? ['(prefers-color-scheme: dark)', '(max-width: 47.99rem)'].map((query) =>
            window.matchMedia(query),
          )
        : [];
    queries.forEach((query) => {
      query.addEventListener('change', sync);
    });
    return () => {
      observer.disconnect();
      queries.forEach((query) => {
        query.removeEventListener('change', sync);
      });
    };
  }, []);
}
